/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './order-copy-modal.component.html';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {OrderApiService} from './order-api.service';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';


export interface OrderCopyResult {
  model: {id: string};
  destination: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-order-copy-modal',
  template
})
export class OrderCopyModalComponent implements OnInit {
  model: any;

  readonly onCopied = new Subject<OrderCopyResult>();

  originalName = '';
  order: any = {};
  hasBudgets = false;
  copyBudget = true;
  copyLineItems = true;
  copyTargeting = true;
  copyCreatives = true;
  userState = '';
  destination = true;
  disabled = false;
  validationErrors: any[] = [];
  statuses: string[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    private readonly orderApi: OrderApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    private readonly translate: TranslateService,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any
  ) {
    this.statuses = LocalNetworkProfile.isPureMarketplacePlatform()
      ? ['PROPOSED', '', 'APPROVED', 'PAUSED']
      : ['', 'PROPOSED', 'SUBMITTED', 'RESERVED', 'APPROVED', 'PAUSED'];
    this.userState = LocalNetworkProfile.isPureMarketplacePlatform() ? 'PROPOSED' : '';
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.order = _.cloneDeep(this.model || {});
    this.order.name = nextCopyTitle(this.model?.name || '');
    this.hasBudgets = _.get(this.model, ['budget', 'length'], 0) > 0;
  }

  statusLabel(state: string): string {
    if (state === '') {
      return 'Copy Line Item Has Same Status';
    }
    return this.translate.instant('lineItem.userState.' + state);
  }

  copy(): void {
    this.disabled = true;
    this.validationErrors = [];
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    const copyRequest = this.orderApi.copyOrder(
      this.order,
      newId,
      this.userState,
      this.copyLineItems,
      this.copyTargeting,
      this.copyCreatives,
      this.copyBudget,
      true
    );
    firstValueFrom(copyRequest).then(() => {
      completeCopyModal(this.bsModalRef, this.onCopied, {model: {id: newId}, destination: this.destination});
      this.adnModalLoading.removeModalLoading();
    }, (errors: any) => {
      this.disabled = false;
      this.validationErrors = _.get(errors, ['data', 'errors'], []);
      this.adnModalLoading.removeModalLoading();
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
